import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { Moment } from 'moment';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { Snapshot } from '@vi-iot/client';

import {
  FeatureService,
  Gateway,
  InstallationDetailsStore,
  RemoteDiagnosticsSnapshotsStore,
  RemoteDiagnosticsStore,
} from '@store';

import { SnapshotDialogComponent } from '../snapshot-dialog/snapshot-dialog.component';

@Component({
  selector: 'app-snapshot-list',
  templateUrl: './snapshot-list.component.html',
  styleUrls: ['./snapshot-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnapshotListComponent implements OnInit, OnDestroy {
  isActiveSessionAvailable: boolean;
  gatewayProperties$: Observable<Gateway>;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  private installationId: number;
  private gatewaySerial: string;

  constructor(
    public remoteDiagnosticsStore: RemoteDiagnosticsStore,
    public remoteDiagnosticsSnapshotsStore: RemoteDiagnosticsSnapshotsStore,
    private featureService: FeatureService,
    private installationDetailsStore: InstallationDetailsStore,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {}

  displayedSnapshots: Snapshot[] = [];

  ngOnInit(): void {
    this.gatewayProperties$ = this.featureService.selectGatewayProperties();

    this.activatedRoute.params.subscribe((param) => {
      this.installationId = +param['id'];
      this.remoteDiagnosticsSnapshotsStore.fetchSnapshots(this.installationId);
    });

    this.installationDetailsStore.gatewaySerial$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((serial) => {
        this.gatewaySerial = serial;
      });

    this.remoteDiagnosticsSnapshotsStore.snapshotsFiltered$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((snapshots) => {
        this.displayedSnapshots = snapshots;
      });

    this.remoteDiagnosticsStore
      .isActiveSessionAvailable(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isActiveSessionAvailable) => {
        this.isActiveSessionAvailable = isActiveSessionAvailable;
        this.cdr.detectChanges();
      });

    this.remoteDiagnosticsSnapshotsStore.setFilteredDate(moment());
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.remoteDiagnosticsSnapshotsStore.showLessSnapshots();
  }

  createSnapshot(): void {
    this.resetValues();

    this.dialog.open(SnapshotDialogComponent, {
      disableClose: true,
      data: { installationId: this.installationId, gatewaySerial: this.gatewaySerial },
      panelClass: 'snapshot-dialog',
    });
  }

  changeDate(date: Moment): void {
    this.remoteDiagnosticsSnapshotsStore.setFilteredDate(date);

    this.remoteDiagnosticsSnapshotsStore.selectSnapshot(null);
    this.remoteDiagnosticsSnapshotsStore.clearDateOfTheValuesRead();
  }

  toggleShowMoreBackups(): void {
    this.remoteDiagnosticsSnapshotsStore.isShowingMoreSnapshots$
      .pipe(take(1))
      .subscribe((showMore) => {
        if (!showMore) {
          this.remoteDiagnosticsSnapshotsStore.showMoreSnapshots();
        } else {
          this.remoteDiagnosticsSnapshotsStore.showLessSnapshots();
        }
      });
  }

  private resetValues(): void {
    this.remoteDiagnosticsSnapshotsStore.selectSnapshot(null);
    this.remoteDiagnosticsSnapshotsStore.clearDateOfTheValuesRead();
  }
}
